import { Link, useParams } from 'react-router-dom';
import usePageMeta from '../lib/usePageMeta';
import PageHero from '../components/PageHero';
import Icon from '../components/Icon';
import ProjectCard, { ProjectVisual } from '../components/ProjectCard';
import { SectionHead } from '../components/Blocks';
import { PROJECTS, PROJECT_BY_SLUG, SERVICE_BY_SLUG, QUOTES } from '../data';
import NotFound from './NotFound';

export default function CaseStudy() {
  const { slug } = useParams();
  const p = PROJECT_BY_SLUG[slug];
  usePageMeta(p ? `${p.name} — Case Study` : 'Not found', p ? `${p.name}: ${p.desc} ${p.challenge}` : '');
  if (!p) return <NotFound />;

  const idx = PROJECTS.indexOf(p);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];
  const more = PROJECTS.filter((x) => x.slug !== p.slug).slice(0, 3);
  const quote = QUOTES[idx % QUOTES.length];

  return (
    <>
      <PageHero eyebrow={`${p.cat} · ${p.tag}`} title={p.name} description={p.desc} crumbs={[{ label: 'Portfolio', to: '/portfolio' }, { label: p.name }]} />

      <section className="section bg-white">
        <div className="container-x">
          <div className="overflow-hidden rounded-[2rem] shadow-2xl" data-anim="zoom"><ProjectVisual project={p} index={idx} className="h-72 sm:h-96" /></div>

          <dl className="mt-10 grid grid-cols-2 gap-6 rounded-3xl bg-brand-soft p-8 md:grid-cols-4" data-anim="up">
            {[['Client', p.client], ['Industry / type', p.tag], ['Year', p.year], ['Duration', p.duration]].map(([k, v]) => (
              <div key={k}><dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{k}</dt><dd className="mt-1 font-display text-lg font-bold text-ink">{v}</dd></div>
            ))}
          </dl>

          <div className="mt-16 grid gap-14 lg:grid-cols-2">
            <div data-anim="left">
              <span className="eyebrow">The challenge</span>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">{p.challenge}</p>
            </div>
            <div data-anim="right">
              <span className="eyebrow">Our solution</span>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">{p.solution}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-ink text-white">
        <div className="grid-bg pointer-events-none absolute inset-0" />
        <div className="container-x relative">
          <SectionHead dark eyebrow="Results" title="The outcome" />
          <div className="mt-12 grid gap-6 md:grid-cols-3" data-anim="zoom" data-stagger>
            {p.results.map((r) => (
              <div key={r.label} className="rounded-3xl border border-white/10 bg-white/[0.05] p-8 text-center">
                <p className="font-display text-5xl font-bold text-brand-lime">{r.value}</p>
                <p className="mt-2 text-white/70">{r.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x grid gap-14 lg:grid-cols-2">
          <div data-anim="left">
            <h2 className="h2 text-ink">Services we provided</h2>
            <div className="mt-8 space-y-3">
              {p.services.map((sl) => {
                const s = SERVICE_BY_SLUG[sl];
                return (
                  <Link key={sl} to={`/services/${sl}`} className="group flex items-center gap-4 rounded-2xl border border-brand/10 bg-brand-soft/50 p-4 transition-colors hover:bg-brand-soft">
                    <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand text-white"><Icon name={s.icon} /></span>
                    <span className="flex-1 font-semibold text-ink">{s.title}</span>
                    <Icon name="arrow" className="h-5 w-5 text-brand transition-transform group-hover:translate-x-1" />
                  </Link>
                );
              })}
            </div>
            <h3 className="mt-10 text-lg font-bold text-ink">Technology</h3>
            <div className="mt-3 flex flex-wrap gap-2">{p.tech.map((t) => <span key={t} className="rounded-full bg-brand-soft px-4 py-1.5 text-sm font-medium text-brand">{t}</span>)}</div>
          </div>
          <figure className="rounded-3xl bg-gradient-to-br from-brand to-ink p-8 text-white sm:p-10" data-anim="right">
            <Icon name="quote" className="h-10 w-10 text-brand" />
            <blockquote className="mt-5 font-display text-xl leading-relaxed sm:text-2xl">“{quote.quote}”</blockquote>
            <figcaption className="mt-6"><span className="block font-semibold">{quote.name}</span><span className="text-sm text-white/60">{quote.role}</span></figcaption>
          </figure>
        </div>
      </section>

      <section className="section bg-brand-soft">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <h2 className="h2 text-ink" data-anim="left">More projects</h2>
            <Link to={`/portfolio/${next.slug}`} className="btn-green self-start" data-anim="right">Next: {next.name} <Icon name="arrow" className="h-4 w-4" /></Link>
          </div>
          <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-anim="up" data-stagger>
            {more.map((m, i) => <ProjectCard key={m.slug} project={m} index={i + 1} />)}
          </div>
        </div>
      </section>
    </>
  );
}
